/**
 * activityLogger — logger client-side de actividad de usuarios (GH #21 / #82).
 *
 * Persiste eventos en MariaDB vía POST /api/activity. Es FIRE-AND-FORGET:
 * nunca lanza, nunca await bloqueante para la UI, y si el POST falla se ignora
 * silenciosamente (a lo sumo un console.debug). La UX jamás debe romperse por
 * tracking.
 *
 * Reusa la instancia axios `api` (src/utils/api.ts) para heredar:
 *   - token de Firebase Auth (Authorization),
 *   - headers x-client-id / x-client-name,
 *   - baseURL correcta (dev/staging/prod).
 *
 * NUNCA se envía info sensible: solo event_type, page/action y metadata acotada
 * (método, status, path). El backend además whitelistea metadata.
 */

import api from './api';

export type ActivityEventType = 'login' | 'pageview' | 'action';

interface ActivityMetadata {
  method?: string;
  status?: number;
  path?: string;
  durationMs?: number;
  referrer?: string;
}

interface LogActivityArgs {
  eventType: ActivityEventType;
  page?: string | null;
  action?: string | null;
  metadata?: ActivityMetadata | null;
}

/**
 * Envía un evento de actividad. Fire-and-forget: no devuelve promesa útil y
 * swallowea cualquier error. Seguro de llamar desde cualquier parte de la UI.
 */
export function logActivity({ eventType, page = null, action = null, metadata = null }: LogActivityArgs): void {
  try {
    // No await: dejamos la promesa correr en background y capturamos el reject
    // para que no genere unhandledrejection.
    api
      .post('/api/activity', {
        event_type: eventType,
        page: page ?? undefined,
        action: action ?? undefined,
        metadata: metadata ?? undefined,
      })
      .catch((err) => {
        // Silencioso por diseño. Solo debug para no ensuciar la consola.
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.debug('[activityLogger] post failed (ignored):', err?.message || err);
        }
      });
  } catch (err) {
    // Cualquier error sincrónico (ej. api no inicializada) también se ignora.
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[activityLogger] threw (ignored):', err);
    }
  }
}

/** Helpers semánticos para los 3 tipos de evento. */
export const activity = {
  login(metadata?: ActivityMetadata) {
    logActivity({ eventType: 'login', metadata });
  },
  pageview(page: string, metadata?: ActivityMetadata) {
    logActivity({ eventType: 'pageview', page, metadata });
  },
  action(action: string, metadata?: ActivityMetadata) {
    logActivity({ eventType: 'action', action, metadata });
  },
};

export default activity;
